import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Mail, Phone, Calendar, TrendingUp, ShoppingCart,
  DollarSign, MapPin, Check, X, Shield, ChevronDown, CreditCard,
  Save, Loader2, AlertTriangle, Plus, Star,
} from 'lucide-react';
import api from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Card from '@/components/ui/Card';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

interface Address {
  id: string;
  label: string | null;
  first_name: string;
  last_name: string;
  company: string | null;
  address_line1: string;
  address_line2: string | null;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  phone: string | null;
  is_default_shipping: boolean;
  is_default_billing: boolean;
}

interface CustomerOrder {
  id: string;
  order_number: string;
  status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled' | 'refunded';
  total: number;
  item_count: number;
  created_at: string;
}

interface Customer {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  avatar_url: string | null;
  role: string;
  created_at: string;
  newsletter_subscribed: boolean;
  order_count: number;
  total_spent: number;
  average_order_value: number;
  orders: CustomerOrder[];
  addresses: Address[];
}

const getStatusBadgeVariant = (
  status: CustomerOrder['status']
): 'green' | 'yellow' | 'red' | 'blue' | 'gray' => {
  switch (status) {
    case 'pending':
      return 'yellow';
    case 'processing':
    case 'shipped':
      return 'blue';
    case 'delivered':
      return 'green';
    case 'cancelled':
    case 'refunded':
      return 'red';
    default:
      return 'gray';
  }
};

const getInitials = (firstName: string, lastName: string): string => {
  return `${(firstName || '?').charAt(0)}${(lastName || '?').charAt(0)}`.toUpperCase();
};

export default function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [roleMenuOpen, setRoleMenuOpen] = useState(false);
  const [isUpdatingRole, setIsUpdatingRole] = useState(false);
  const [isUpdatingNewsletter, setIsUpdatingNewsletter] = useState(false);

  useEffect(() => {
    const fetchCustomer = async () => {
      if (!id) return;

      setIsLoading(true);
      setError(null);

      try {
        const response = await api.get<Customer>(`/admin/customers/${id}`);
        setCustomer(response);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load customer');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCustomer();
  }, [id]);

  // Close role menu on outside click
  useEffect(() => {
    if (!roleMenuOpen) return;
    const handleClick = () => setRoleMenuOpen(false);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [roleMenuOpen]);

  const handleNewsletterToggle = async () => {
    if (!customer) return;
    setIsUpdatingNewsletter(true);
    try {
      await api.patch(`/admin/customers/${customer.id}/newsletter`, {
        newsletter_subscribed: !customer.newsletter_subscribed,
      });
      setCustomer({ ...customer, newsletter_subscribed: !customer.newsletter_subscribed });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update newsletter status');
    } finally {
      setIsUpdatingNewsletter(false);
    }
  };

  const handleRoleChange = async (newRole: string) => {
    if (!customer || newRole === customer.role) {
      setRoleMenuOpen(false);
      return;
    }

    setIsUpdatingRole(true);
    setRoleMenuOpen(false);

    try {
      await api.patch(`/admin/customers/${customer.id}/role`, { role: newRole });
      setCustomer({ ...customer, role: newRole });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role');
    } finally {
      setIsUpdatingRole(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen" role="status" aria-live="polite">
        <LoadingSpinner />
        <span className="sr-only">Loading customer details...</span>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="space-y-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/admin/customers')}
          aria-label="Back to customers"
        >
          <ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />
          Back to Customers
        </Button>
        <Card padding="lg">
          <div className="text-center text-red-600" role="alert">
            {error || 'Customer not found'}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/admin/customers')}
          aria-label="Back to customers"
        >
          <ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />
          Back
        </Button>
      </div>

      {/* Profile Card */}
      <Card padding="lg">
        <div className="flex items-start gap-6">
          {/* Avatar */}
          <div className="flex-shrink-0">
            {customer.avatar_url ? (
              <img
                src={customer.avatar_url}
                alt={`${customer.first_name} ${customer.last_name}`}
                className="h-24 w-24 rounded-full object-cover"
              />
            ) : (
              <div className="h-24 w-24 rounded-full bg-blue-100 flex items-center justify-center">
                <span className="text-2xl font-semibold text-blue-600">
                  {getInitials(customer.first_name, customer.last_name)}
                </span>
              </div>
            )}
          </div>

          {/* Profile Info */}
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold text-gray-900">
                {customer.first_name} {customer.last_name}
              </h1>

              {/* Role Dropdown */}
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setRoleMenuOpen(!roleMenuOpen);
                  }}
                  disabled={isUpdatingRole}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  {isUpdatingRole ? (
                    <LoadingSpinner />
                  ) : (
                    <>
                      <Shield className="h-4 w-4" aria-hidden="true" />
                      {customer.role === 'admin' ? 'Admin' : 'User'}
                      <ChevronDown className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />
                    </>
                  )}
                </button>

                {roleMenuOpen && (
                  <div className="absolute left-0 z-10 mt-1 w-44 rounded-lg border border-gray-200 bg-white shadow-lg">
                    <div className="py-1">
                      <button
                        onClick={() => handleRoleChange('user')}
                        className={`flex w-full items-center gap-2 px-4 py-2 text-sm transition-colors hover:bg-gray-50 ${
                          customer.role === 'user' ? 'text-blue-600 font-medium' : 'text-gray-700'
                        }`}
                      >
                        {customer.role === 'user' && (
                          <Check className="h-4 w-4" aria-hidden="true" />
                        )}
                        <span className={customer.role === 'user' ? '' : 'ml-6'}>User</span>
                      </button>
                      <button
                        onClick={() => handleRoleChange('admin')}
                        className={`flex w-full items-center gap-2 px-4 py-2 text-sm transition-colors hover:bg-gray-50 ${
                          customer.role === 'admin' ? 'text-blue-600 font-medium' : 'text-gray-700'
                        }`}
                      >
                        {customer.role === 'admin' && (
                          <Check className="h-4 w-4" aria-hidden="true" />
                        )}
                        <span className={customer.role === 'admin' ? '' : 'ml-6'}>Admin</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-4 space-y-2">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Mail className="h-4 w-4" aria-hidden="true" />
                <a
                  href={`mailto:${customer.email}`}
                  className="hover:text-blue-600 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded"
                >
                  {customer.email}
                </a>
              </div>
              {customer.phone && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Phone className="h-4 w-4" aria-hidden="true" />
                  <a
                    href={`tel:${customer.phone}`}
                    className="hover:text-blue-600 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded"
                  >
                    {customer.phone}
                  </a>
                </div>
              )}
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Calendar className="h-4 w-4" aria-hidden="true" />
                <span>Member since {formatDate(customer.created_at)}</span>
              </div>
              <button
                onClick={handleNewsletterToggle}
                disabled={isUpdatingNewsletter}
                className="flex items-center gap-2 text-sm rounded px-2 py-1 -mx-2 transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
              >
                {isUpdatingNewsletter ? (
                  <LoadingSpinner />
                ) : customer.newsletter_subscribed ? (
                  <>
                    <Check className="h-4 w-4 text-green-600" aria-hidden="true" />
                    <span className="text-green-600">Subscribed to newsletter</span>
                  </>
                ) : (
                  <>
                    <X className="h-4 w-4 text-gray-400" aria-hidden="true" />
                    <span className="text-gray-600">Not subscribed to newsletter</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </Card>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card padding="lg">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <ShoppingCart className="h-6 w-6 text-blue-600" aria-hidden="true" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Orders</p>
              <p className="text-2xl font-bold text-gray-900">
                {customer.order_count}
              </p>
            </div>
          </div>
        </Card>

        <Card padding="lg">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <DollarSign className="h-6 w-6 text-green-600" aria-hidden="true" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Spent</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(customer.total_spent)}
              </p>
            </div>
          </div>
        </Card>

        <Card padding="lg">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <TrendingUp className="h-6 w-6 text-purple-600" aria-hidden="true" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Average Order Value</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(customer.average_order_value)}
              </p>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Orders */}
        <div className="lg:col-span-2">
          <Card title="Recent Orders" padding="none">
            {customer.orders.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                No orders yet
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                      >
                        Order
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                      >
                        Date
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                      >
                        Status
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500"
                      >
                        Items
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500"
                      >
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {customer.orders.map((order) => (
                      <tr
                        key={order.id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Link
                            to={`/admin/orders/${order.id}`}
                            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded"
                          >
                            #{order.order_number}
                          </Link>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(order.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge variant={getStatusBadgeVariant(order.status)}>
                            {order.status.charAt(0).toUpperCase() +
                              order.status.slice(1)}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                          {order.item_count}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                          {formatCurrency(order.total)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>

        {/* Addresses + Subscription */}
        <div className="space-y-6">
          <SubscriptionTierSection customerId={customer.id} />
          <PasswordResetSection customerId={customer.id} />
          <FeaturedCustomerSection customerId={customer.id} />
          <Card title="Addresses" padding="lg">
            {customer.addresses.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No saved addresses
              </div>
            ) : (
              <div className="space-y-6">
                {customer.addresses.map((address) => (
                  <div
                    key={address.id}
                    className="border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <MapPin
                          className="h-4 w-4 text-gray-400"
                          aria-hidden="true"
                        />
                        <span className="text-sm font-medium text-gray-900">
                          {address.label || 'Address'}
                        </span>
                      </div>
                      <div className="flex gap-1">
                        {address.is_default_shipping && (
                          <Badge variant="blue">Shipping</Badge>
                        )}
                        {address.is_default_billing && (
                          <Badge variant="green">Billing</Badge>
                        )}
                      </div>
                    </div>
                    <address className="text-sm text-gray-700 not-italic">
                      <p className="font-medium">
                        {address.first_name} {address.last_name}
                      </p>
                      {address.company && <p>{address.company}</p>}
                      <p>{address.address_line1}</p>
                      {address.address_line2 && <p>{address.address_line2}</p>}
                      <p>
                        {address.city}, {address.state} {address.postal_code}
                      </p>
                      <p>{address.country}</p>
                      {address.phone && <p className="mt-1">{address.phone}</p>}
                    </address>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

interface SubscriptionRow {
  id: string;
  user_id: string;
  plan_id: string;
  status: string;
  stripe_subscription_id: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  plan_name: string;
  plan_slug: string;
  plan_tier_level: number;
}

interface PlanRow {
  id: string;
  name: string;
  slug: string;
  tier_level: number;
  price: string;
  interval: string;
  is_active: boolean;
}

function SubscriptionTierSection({ customerId }: { customerId: string }) {
  const [subscription, setSubscription] = useState<SubscriptionRow | null>(null);
  const [plans, setPlans] = useState<PlanRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [draftPlanId, setDraftPlanId] = useState<string | null>(null);
  const [draftComp, setDraftComp] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingMsg, setSavingMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await api.get<{ subscription: SubscriptionRow | null; plans: PlanRow[] }>(
        `/admin/customers/${customerId}/subscription`
      );
      setSubscription(res.subscription);
      setPlans(res.plans);
      setDraftPlanId(null);
      setDraftComp(res.subscription?.status === 'complimentary');
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load subscription');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { void load(); }, [load]);

  async function handleSave() {
    const planId = draftPlanId ?? subscription?.plan_id ?? null;
    if (!planId) return;
    setSaving(true);
    setSavingMsg('');
    try {
      const res = await api.put<{
        subscription: SubscriptionRow;
        override_masked_by_subscription: boolean;
      }>(`/admin/customers/${customerId}/subscription`, {
        plan_id: planId,
        status: draftComp ? 'complimentary' : 'active',
      });
      const planRow = plans.find((p) => p.id === res.subscription.plan_id);
      setSubscription({
        ...res.subscription,
        plan_name: planRow?.name ?? '',
        plan_slug: planRow?.slug ?? '',
        plan_tier_level: planRow?.tier_level ?? 0,
        current_period_end: res.subscription.current_period_end ?? null,
        cancel_at_period_end: res.subscription.cancel_at_period_end ?? false,
      });
      setDraftPlanId(null);
      setDraftComp(res.subscription.status === 'complimentary');
      setSavingMsg(
        res.override_masked_by_subscription
          ? `Saved — but an active Stripe subscription will overwrite this on the next webhook.`
          : draftComp
            ? `Saved — complimentary (free) access granted.`
            : `Saved — subscription tier updated.`
      );
    } catch (err) {
      setSavingMsg(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function handleClear() {
    if (!subscription) return;
    if (!window.confirm('Cancel this customer\'s subscription? They will lose access to gated features on next render.')) return;
    setSaving(true);
    setSavingMsg('');
    try {
      await api.delete(`/admin/customers/${customerId}/subscription`);
      setSubscription(null);
      setDraftPlanId(null);
      setSavingMsg('Subscription cancelled.');
    } catch (err) {
      setSavingMsg(err instanceof Error ? err.message : 'Failed to cancel');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card title="Subscription Tier" padding="lg">
      {loading && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      )}

      {loadError && (
        <div className="p-3 rounded border border-red-200 bg-red-50 text-sm text-red-900 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{loadError}</span>
        </div>
      )}

      {!loading && !loadError && plans.length === 0 && (
        <div className="text-sm text-gray-600">
          No subscription plans defined for this site yet. Create plans in{' '}
          <Link to="/admin/subscription-plans" className="text-sage-700 underline">
            Subscription Plans
          </Link>{' '}
          before granting tiers here.
        </div>
      )}

      {!loading && !loadError && plans.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-600">Current:</span>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
              {subscription ? subscription.plan_name : 'No subscription'}
            </span>
            {subscription?.status && subscription.status !== 'active' && (
              <span className="text-xs text-gray-500 capitalize">({subscription.status})</span>
            )}
          </div>

          {subscription?.stripe_subscription_id && (
            <div className="p-3 rounded border border-amber-200 bg-amber-50 text-xs text-amber-900">
              This customer has an active Stripe subscription
              (<span className="font-mono">{subscription.stripe_subscription_id}</span>).
              Overriding here updates the row immediately, but the next Stripe webhook may
              overwrite it. Use this for testing or for customers without a live billing setup.
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={draftPlanId ?? subscription?.plan_id ?? ''}
              onChange={(e) => setDraftPlanId(e.target.value)}
              disabled={saving}
              className="border border-gray-300 rounded px-3 py-1.5 text-sm bg-white"
            >
              {!subscription && <option value="">Select a plan…</option>}
              {plans.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} (tier {p.tier_level})
                </option>
              ))}
            </select>
            <label className="flex items-center gap-1.5 text-sm text-gray-700 select-none">
              <input
                type="checkbox"
                checked={draftComp}
                onChange={(e) => setDraftComp(e.target.checked)}
                disabled={saving}
              />
              Complimentary (free, no billing)
            </label>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={
                saving ||
                !(draftPlanId ?? subscription?.plan_id) ||
                ((!draftPlanId || draftPlanId === subscription?.plan_id) &&
                  draftComp === (subscription?.status === 'complimentary'))
              }
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Save</>}
            </Button>
            {subscription && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleClear}
                disabled={saving}
              >
                Cancel
              </Button>
            )}
          </div>

          {savingMsg && (
            <p
              className={`text-xs ${
                savingMsg.toLowerCase().includes('fail')
                  ? 'text-red-600'
                  : savingMsg.toLowerCase().includes('overwrite')
                  ? 'text-amber-700'
                  : 'text-green-700'
              }`}
            >
              {savingMsg}
            </p>
          )}
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Password reset — for the customer who can't use "forgot password" because
// mail to their address doesn't reach them.
//
// Issues a link rather than setting a password: the admin never handles a
// plaintext credential, the token is single-use and expires, and the customer
// still picks their own. Copy it out and send it however you already talk to
// them. Deliberately no "reveal current password" — there isn't one to reveal,
// only a hash.
// ---------------------------------------------------------------------------
function PasswordResetSection({ customerId }: { customerId: string }) {
  const [link, setLink] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  async function generate() {
    setBusy(true);
    setError('');
    setCopied(false);
    try {
      const res = await api.post<{ link: string }>(
        `/admin/customers/${customerId}/password-reset-link`,
        {}
      );
      setLink(res.link);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create a reset link');
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked (insecure context, permissions) — the link is on
      // screen and selectable, so this is a convenience, not the only route.
      setError('Copy failed — select the link and copy it manually.');
    }
  }

  return (
    <Card title="Password" padding="lg">
      <div className="space-y-3">
        <p className="text-sm text-gray-600">
          Generates a single-use link this customer can use to set a new
          password. Use it when they can&apos;t receive the self-serve reset
          email.
        </p>

        {error && (
          <div className="p-3 rounded border border-red-200 bg-red-50 text-sm text-red-900 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {link ? (
          <div className="space-y-2">
            <label
              htmlFor="reset-link"
              className="block text-xs font-medium uppercase tracking-wide text-gray-500"
            >
              Reset link
            </label>
            <textarea
              id="reset-link"
              readOnly
              value={link}
              rows={3}
              onFocus={(e) => e.currentTarget.select()}
              className="w-full rounded border border-gray-300 bg-gray-50 p-2 font-mono text-xs text-gray-800"
            />
            <div className="flex items-center gap-2">
              <Button type="button" variant="secondary" size="sm" onClick={() => void copy()}>
                {copied ? (
                  <>
                    <Check className="w-4 h-4 mr-1" /> Copied
                  </>
                ) : (
                  'Copy link'
                )}
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => void generate()} disabled={busy}>
                Generate a new one
              </Button>
            </div>
            <p className="text-xs text-gray-500">
              Single use, and it expires. Send it over a channel you trust —
              anyone holding it can set this account&apos;s password.
            </p>
          </div>
        ) : (
          <Button type="button" onClick={() => void generate()} disabled={busy}>
            {busy ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" /> Generating…
              </>
            ) : (
              'Generate password reset link'
            )}
          </Button>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Featured Customer — "Feature this customer" on the public case-studies page.
//
// Mirrors SubscriptionTierSection: load → draft → Save/Remove via
// /admin/customers/:id/showcase. Uses the admin gray/blue chrome (NOT the
// public sage theme). The two author inputs (name + title) are joined into the
// single `author` string the public Story renders. Save is blocked when
// featured but brand/headline are empty (client mirror of the backend refine).
// ---------------------------------------------------------------------------

interface ShowcaseMetric {
  value: string;
  label: string;
}

interface ShowcaseRow {
  id: string;
  user_id: string;
  is_featured: boolean;
  brand: string | null;
  site_url: string | null;
  logo_url: string | null;
  headline: string | null;
  summary: string | null;
  quote: string | null;
  author: string | null;
  metrics: ShowcaseMetric[];
  sort_order: number;
}

function splitAuthor(author: string): { name: string; title: string } {
  if (!author) return { name: '', title: '' };
  const idx = author.indexOf(', ');
  if (idx === -1) return { name: author, title: '' };
  return { name: author.slice(0, idx), title: author.slice(idx + 2) };
}

const showcaseInputClass =
  'w-full border border-gray-300 rounded px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500';

export function FeaturedCustomerSection({ customerId }: { customerId: string }) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [savingMsg, setSavingMsg] = useState('');
  const [existing, setExisting] = useState<ShowcaseRow | null>(null);

  // Draft state
  const [featured, setFeatured] = useState(false);
  const [brand, setBrand] = useState('');
  const [siteUrl, setSiteUrl] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [headline, setHeadline] = useState('');
  const [summary, setSummary] = useState('');
  const [quote, setQuote] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [authorTitle, setAuthorTitle] = useState('');
  const [metrics, setMetrics] = useState<ShowcaseMetric[]>([]);
  const [sortOrder, setSortOrder] = useState(0);

  const hydrate = useCallback((row: ShowcaseRow | null) => {
    setExisting(row);
    setFeatured(row?.is_featured ?? false);
    setBrand(row?.brand ?? '');
    setSiteUrl(row?.site_url ?? '');
    setLogoUrl(row?.logo_url ?? '');
    setHeadline(row?.headline ?? '');
    setSummary(row?.summary ?? '');
    setQuote(row?.quote ?? '');
    const { name, title } = splitAuthor(row?.author ?? '');
    setAuthorName(name);
    setAuthorTitle(title);
    setMetrics(Array.isArray(row?.metrics) ? row.metrics : []);
    setSortOrder(row?.sort_order ?? 0);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await api.get<{ showcase: ShowcaseRow | null }>(
        `/admin/customers/${customerId}/showcase`
      );
      hydrate(res.showcase);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load showcase');
    } finally {
      setLoading(false);
    }
  }, [customerId, hydrate]);

  useEffect(() => {
    void load();
  }, [load]);

  const missingRequired = featured && (!brand.trim() || !headline.trim());
  const canSave = !saving && !missingRequired;

  async function handleSave() {
    setSaving(true);
    setSavingMsg('');
    const author = [authorName.trim(), authorTitle.trim()].filter(Boolean).join(', ');
    try {
      const res = await api.put<{ showcase: ShowcaseRow }>(
        `/admin/customers/${customerId}/showcase`,
        {
          is_featured: featured,
          brand: brand.trim(),
          site_url: siteUrl.trim(),
          logo_url: logoUrl.trim(),
          headline: headline.trim(),
          summary: summary.trim(),
          quote: quote.trim(),
          author,
          metrics: metrics
            .filter((m) => m.value.trim() && m.label.trim())
            .slice(0, 3)
            .map((m) => ({ value: m.value.trim(), label: m.label.trim() })),
          sort_order: sortOrder,
        }
      );
      hydrate(res.showcase);
      setSavingMsg(
        featured
          ? 'Saved — this customer is now featured on the case-studies page.'
          : 'Saved — draft kept (not featured).'
      );
    } catch (err) {
      setSavingMsg(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function handleClear() {
    if (!existing) return;
    if (!window.confirm('Remove this customer from the showcase? This deletes the saved story.')) return;
    setSaving(true);
    setSavingMsg('');
    try {
      await api.delete(`/admin/customers/${customerId}/showcase`);
      hydrate(null);
      setSavingMsg('Removed from showcase.');
    } catch (err) {
      setSavingMsg(err instanceof Error ? err.message : 'Failed to remove');
    } finally {
      setSaving(false);
    }
  }

  function updateMetric(i: number, patch: Partial<ShowcaseMetric>) {
    setMetrics((prev) => prev.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));
  }

  return (
    <Card title="Featured Customer" padding="lg">
      {loading && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      )}

      {loadError && (
        <div className="p-3 rounded border border-red-200 bg-red-50 text-sm text-red-900 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{loadError}</span>
        </div>
      )}

      {!loading && !loadError && (
        <div className="space-y-4">
          <label className="flex items-center gap-2 text-sm text-gray-700 select-none">
            <input
              type="checkbox"
              checked={featured}
              onChange={(e) => setFeatured(e.target.checked)}
              disabled={saving}
            />
            <Star className="h-4 w-4 text-amber-500" aria-hidden="true" />
            Feature this customer on the case-studies page
          </label>

          {featured && (
            <div className="border-t border-gray-200 pt-4 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Brand / display name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={brand}
                  onChange={(e) => setBrand(e.target.value)}
                  disabled={saving}
                  placeholder="Axiss Support"
                  className={showcaseInputClass}
                />
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Site URL</label>
                  <input
                    type="url"
                    value={siteUrl}
                    onChange={(e) => setSiteUrl(e.target.value)}
                    disabled={saving}
                    placeholder="https://axiss.support"
                    className={showcaseInputClass}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Logo URL</label>
                  <input
                    type="url"
                    value={logoUrl}
                    onChange={(e) => setLogoUrl(e.target.value)}
                    disabled={saving}
                    placeholder="https://…/logo.png"
                    className={showcaseInputClass}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Headline <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={headline}
                  onChange={(e) => setHeadline(e.target.value)}
                  disabled={saving}
                  placeholder="How Axiss cut response time 60%"
                  className={showcaseInputClass}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Summary</label>
                <textarea
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  disabled={saving}
                  rows={3}
                  className={showcaseInputClass}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Quote</label>
                <textarea
                  value={quote}
                  onChange={(e) => setQuote(e.target.value)}
                  disabled={saving}
                  rows={3}
                  className={showcaseInputClass}
                />
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Quote author — name</label>
                  <input
                    type="text"
                    value={authorName}
                    onChange={(e) => setAuthorName(e.target.value)}
                    disabled={saving}
                    placeholder="Jane Doe"
                    className={showcaseInputClass}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Quote author — title</label>
                  <input
                    type="text"
                    value={authorTitle}
                    onChange={(e) => setAuthorTitle(e.target.value)}
                    disabled={saving}
                    placeholder="Founder"
                    className={showcaseInputClass}
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-medium text-gray-600">Metrics (up to 3)</label>
                  {metrics.length < 3 && (
                    <button
                      type="button"
                      onClick={() => setMetrics((prev) => [...prev, { value: '', label: '' }])}
                      disabled={saving}
                      className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50"
                    >
                      <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Add metric
                    </button>
                  )}
                </div>
                <div className="space-y-2">
                  {metrics.map((m, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={m.value}
                        onChange={(e) => updateMetric(i, { value: e.target.value })}
                        disabled={saving}
                        placeholder="60%"
                        className={`${showcaseInputClass} w-24`}
                      />
                      <input
                        type="text"
                        value={m.label}
                        onChange={(e) => updateMetric(i, { label: e.target.value })}
                        disabled={saving}
                        placeholder="Faster replies"
                        className={showcaseInputClass}
                      />
                      <button
                        type="button"
                        onClick={() => setMetrics((prev) => prev.filter((_, idx) => idx !== i))}
                        disabled={saving}
                        aria-label="Remove metric"
                        className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-50"
                      >
                        <X className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </div>
                  ))}
                  {metrics.length === 0 && (
                    <p className="text-xs text-gray-400">No metrics yet — add up to three (e.g. "60%" / "Faster replies").</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Sort order <span className="text-gray-400">(lower shows first; row 0 = spotlight)</span>
                </label>
                <input
                  type="number"
                  min={0}
                  value={sortOrder}
                  onChange={(e) => setSortOrder(Math.max(0, parseInt(e.target.value, 10) || 0))}
                  disabled={saving}
                  className={`${showcaseInputClass} w-24`}
                />
              </div>
            </div>
          )}

          <div className="flex items-center gap-2">
            <Button size="sm" onClick={handleSave} disabled={!canSave}>
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Save className="w-4 h-4 mr-1" /> Save
                </>
              )}
            </Button>
            {existing && (
              <Button size="sm" variant="outline" onClick={handleClear} disabled={saving}>
                Remove from showcase
              </Button>
            )}
          </div>

          {missingRequired && (
            <p className="text-xs text-amber-700">
              Brand and headline are required to feature a customer.
            </p>
          )}
          {savingMsg && (
            <p
              className={`text-xs ${
                savingMsg.toLowerCase().includes('fail') ? 'text-red-600' : 'text-green-700'
              }`}
            >
              {savingMsg}
            </p>
          )}
        </div>
      )}
    </Card>
  );
}
